// Doprava a platba (`/doprava-platba/`) — handover „Doprava a platba v2"
// (`design_handoff_doprava_platba/README.md`, artboardy `design/Main.dc.html`
// a `design/Mobil.dc.html`), schváleno 2026-09-05. Nahrazuje první kolo návrhu
// (karty dopravců s logy, akordeon „Podrobnosti", trust pás, tint CTA pás,
// banner dopravy zdarma) — narazí-li budoucí session na `screens/10-doprava-
// platba.md` nebo mockupy „Doprava a platba*.dc.html" a bude chtít ty bloky
// vrátit, NEDĚLAT: jsou to zamítnuté podoby.
//
// PROČ vznikla znovu: stránka byla jedna z prvních a od hmotnostního tarifu
// (2026-09-02, pásma po 10 kg od 2026-09-05) lhala — ceny, limity dopravců
// i sobotní/nedělní závozy na ní nebyly vůbec. Úkolem stránky je vysvětlit
// celou logiku dopravy: kolik zaplatím podle hmotnosti, kdy to dorazí a kdo
// to veze.
//
// ZDROJ ČÍSEL je `shippingModel.js` (`WEIGHT_BANDS`, `WEIGHT_BAND_PRICES`,
// `shippingPriceTable()`, `BOX_LIMITS`, `FREE_SHIP_*`; hodnoty z manifestu shopu
// `shipping`) = zrcadlo tarifu `hmotnostni` v `tools/admin/doprava-cenik.mjs`,
// a `deliveryTimeline.js`
// (`FAST_RULES`) s `dispatchEta.js` (uzávěrka `DISPATCH_CUTOFF`). Stránka nic
// nepočítá znovu a nic nepíše natvrdo: co ukazuje ceník, platí i v košíku, protože obojí čte tytéž
// konstanty. Ukazatel pásem je doslova prvek z pokladny (`weightScale.js`).
//
// ROZHODNUTÍ O OBSAHU (2026-09-05, handover §3 „rozhodnout při implementaci"):
// texty stránky vlastní ŠABLONA, ne administrace. Předchozí verze parsovala
// ceník z `.sp-grid`/`.sp-details` v admin HTML — jenže každá věta téhle
// stránky mluví o číslech, která žijí v repu (tarif, cut-off, okna závozů).
// Rozdělit čísla (repo) a věty o nich (admin) znamená zaručit, že se rozejdou;
// po přeladění pásem 2026-09-05 se to taky stalo. Administrace drží prostý
// textový fallback pro běh bez JS (a SEO text) — schová ho `.bs-ip__native`,
// jakmile naše vrstva stojí. Změna tarifu = přepsat tarif + konstanty modelu,
// stránka se přizpůsobí sama.
//
// Interaktivní je jen kalkulačka (hmotnost × hodnota objednávky → pásmo a cena),
// jinak je stránka statický popis pravidel — živý termín doručení počítá detail
// produktu a košík, ne tahle stránka.
//
// POŘADÍ SEKCÍ (2026-09-05, zadání Tomáše po nasazení v1): hero → ceník po
// pásmech → kdy balík dorazí → dopravci a odběr | platba → KALKULAČKA →
// kontakt. Ruší pořadí z handoveru §1 (kalkulačka tam stála hned pod hero) —
// nevracet, je to schválená úprava (viz README handoveru §6).
//
// LOGA DOPRAVCŮ (2026-09-05): GLS/PPL/Fofr nesou reálná loga
// (`assets/carrier-logos/`, dodal Tomáš) místo dřívějších rastrových PNG
// z admin CDN, které se navíc vykreslovaly deformovaně — `.bs-shp__carrier-side`
// je sloupcový flex, jehož výchozí stretch roztahoval šířku obrázku na celý
// sloupec bez ohledu na aspect ratio (fix: `align-self: flex-start` v
// `_shipping.scss`). Fofr dřív logo neměl vůbec (jen text) — teď ho má taky.

import { icon } from './icon.js';
import { CONTACT, hoursText, phonesOn, telHref } from './headerTools.js';
import { STATIC_ICO } from './instanceUrl.js';
import { FAST_RULES } from './deliveryTimeline.js';
import { DISPATCH_CUTOFF, hourLabel } from './dispatchEta.js';
import { buildWeightScale, weightCurRow, priceText } from './weightScale.js';
import { number, currency } from './money.js';
import {
  OVER_FROM_KG,
  HAS_WEIGHT_BANDS,
  FREE_SHIPPING,
  HAS_PICKUP,
  WEIGHT_BANDS,
  WEIGHT_BAND_PRICES,
  FREE_SHIP_MAX_KG,
  FREE_SHIP_FROM_FALLBACK,
  FREE_SHIP_SCOPE,
  OVER_PRICE,
  BOX_LIMITS,
  ADDR_LIMITS,
  PICKUP_PLACE,
  PICKUP_MAP_URL,
  PICKUP_MAP_LABEL,
  WEIGHT_SCALE_NOTE_A,
  WEIGHT_FOOT_OVER,
  bandOf,
  tableBands,
  shippingPriceTable,
  formatKg,
  paymentIcon,
  shippingPromo,
} from './shippingModel.js';
import { el, link, pageHost, pageIs, markReady, buildHero, sectionHead } from './infoPage.js';
import { asChatTrigger, chatFallbackHref, chatOn } from './supportChat.js';
import LC from '../locale/cs/supportChat.js';
import SHOP from '../../config/shop.js';
import L from '../locale/cs/shippingPage.js';

const NBSP = '\u00A0';
// hranice, nad kterou veze zásilku přeprava pro nadměrné balíky (s pásmy =
// poslední pásmo, u plochého tarifu vlastní číslo z ceníku)
const LAST = OVER_FROM_KG;
// Uzávěrka expedice: Po–Čt jedna (manifest to hlídá), pátek vlastní (14:30 od
// 2026-10-13). Věty i týdenní pás mluví o pátku výslovně (okna GLS soboty a PPL
// neděle začínají a končí páteční uzávěrkou); se stejným časem texty pátek nezmíní.
const CUTOFF = hourLabel(DISPATCH_CUTOFF[1]);
const CUTOFF_FRI = hourLabel(DISPATCH_CUTOFF[5]);
const FREE_FROM = FREE_SHIPPING ? priceText(FREE_SHIP_FROM_FALLBACK) : null;
const KG = NBSP + 'kg';
// Sloupce ceníku: hranice pásem, u plochého tarifu jediný sloupec do
// `OVER_FROM_KG` (S4 #400, `shippingModel.tableBands()`)
const BANDS = tableBands();

// Texty stránky → locale vrstva (`locale/cs|sk/shippingPage.js`, S3 #399); čísla
// jdou do vět parametry z modelu. Locale je per shop, takže věty SK popisují
// plochý tarif SK (S4 #400) a parametry pásem v nich prostě nepoužijí.
const LABEL_EYEBROW = L.eyebrow;
const LABEL_PEREX = L.perex({
  maxKg: FREE_SHIP_MAX_KG,
  freeFrom: FREE_FROM,
  scope: FREE_SHIP_SCOPE,
  cutoff: CUTOFF,
  cutoffFri: CUTOFF_FRI,
  boxPrice: priceText(WEIGHT_BAND_PRICES[0].box),
  addrPrice: priceText(WEIGHT_BAND_PRICES[0].addr),
  overKg: OVER_FROM_KG,
});
// Akce na dopravu (#322): nejlevnější výdejní místo je po dobu akce akční cena
// a hero nese pruh akce (stejné znění jako škála pásem a pruh na detailu).
const PROMO = shippingPromo();
const FACTS = L.facts({
  fromPrice: priceText(PROMO ? Math.min(PROMO.price, WEIGHT_BAND_PRICES[0].box) : WEIGHT_BAND_PRICES[0].box),
  scope: FREE_SHIP_SCOPE,
  freeFrom: FREE_FROM,
  maxKg: FREE_SHIP_MAX_KG,
  cutoff: CUTOFF,
  cutoffFri: CUTOFF_FRI,
  overKg: OVER_FROM_KG,
  overPrice: priceText(OVER_PRICE),
});

// --- kalkulačka -------------------------------------------------------------
// Návrh (handover §4: „funkčně stačí cokoliv, co dá kg"): jedna hodnota pod
// prvním pásmem, jedna v každém dalším a jedna nad limitem, ať zákazník vidí
// všechny stavy ukazatele na dvě kliknutí.
const CALC_CHIPS = [2, 8, 12, 25, 35];
const CALC_DEFAULT_KG = 12;
const CALC_UNDER = L.calcUnder(FREE_FROM);
const CALC_OVER = L.calcOver(FREE_FROM);

// --- ceník ------------------------------------------------------------------
const PRICE_LEAD = L.priceLead({
  maxKgDec: FREE_SHIP_MAX_KG == null ? null : FREE_SHIP_MAX_KG.toFixed(2).replace('.', ','),
  maxKg: FREE_SHIP_MAX_KG,
  nextKg: WEIGHT_BANDS[1],
  overKg: OVER_FROM_KG,
});
const PRICE_FREE_SUM = FREE_SHIPPING ? L.priceFreeSum(FREE_FROM) : null;
const PRICE_FREE_CELL = FREE_SHIPPING ? L.priceFreeCell(FREE_FROM) : null;
const PRICE_NOTE = L.priceNote({ maxKg: FREE_SHIP_MAX_KG, scope: FREE_SHIP_SCOPE, overKg: OVER_FROM_KG });
const DASH = '–';
// Na mobilu se dvě sloučené řádky ceníku (PPL a GLS na adresu mají tytéž ceny
// i popis) skládají do jedné — pět sloupců se na 390 px nevejde a dvakrát týž
// blok by byl jen šum. Klíč = řádek, který se schová; hodnota = řádek, který
// na mobilu dostane druhé jméno.
const MOBILE_MERGE = { 'addr-gls': { into: 'addr-ppl', suffix: L.mergeSuffix } };

// --- termíny ----------------------------------------------------------------
// Zvláštní závozy jsou dohoda klienta s dopravcem (manifest `shipping.fastRules`,
// deliveryTimeline.js): shop bez nich (SK) nemá v týdenním pásu ani v jízdním
// řádu co ukazovat — řádky o sobotě a neděli se nestaví vůbec.
const SAT = FAST_RULES.find((r) => r.key === 'sat') || null;
const SUN = FAST_RULES.find((r) => r.key === 'sun') || null;
const LABEL_DISPATCH = L.dispatch(CUTOFF);
// první pruh skladu kryje čtvrtek a pátek
const LABEL_DISPATCH_FRI = L.dispatchFri({ cutoff: CUTOFF, cutoffFri: CUTOFF_FRI });
// Pruhy týdenního pásu: [start, konec) v půldnech (mřížka 12 sloupců = 6 dní),
// tón a text. `short` je zkrácený popisek pro mobil, kde se do sloupce vejde
// jen slovo — bez něj pruh na mobilu text nenese vůbec.
const WEEK_ROWS = [
  {
    key: 'stock',
    label: L.rowStock,
    bars: [
      { from: 1, to: 5, tone: 'ink', text: LABEL_DISPATCH_FRI },
      { from: 5, to: 9, tone: 'off', text: L.weekend },
      { from: 9, to: 13, tone: 'ink', text: LABEL_DISPATCH },
    ],
  },
]
  .concat(
    SAT
      ? [
          {
            key: 'sat',
            logo: 'gls_logo.svg',
            logoH: 15,
            carrier: SAT.carrier,
            label: L.rowSat,
            bars: [
              { from: 2, to: 4, tone: 'order', text: L.satOrder, short: SAT.carrier },
              { from: 4, to: 5, tone: 'ship', text: L.satShip, short: '' },
              { from: 5, to: 7, tone: 'deliver', text: L.satDeliver, short: L.satDeliverShort },
            ],
          },
        ]
      : []
  )
  .concat(
    SUN
      ? [
          {
            key: 'sun',
            logo: 'ppl_logo.svg',
            logoH: 12,
            carrier: SUN.carrier,
            label: L.rowSun,
            bars: [
              { from: 4, to: 7, tone: 'order', text: L.sunOrder, short: SUN.carrier },
              { from: 7, to: 9, tone: 'ship', text: L.sunShip, short: L.sunShipShort },
              { from: 9, to: 11, tone: 'deliver', text: L.sunDeliver, short: L.sunDeliverShort },
              { from: 11, to: 13, tone: 'off', text: L.others, short: L.othersShort },
            ],
          },
        ]
      : []
  );
// značka pod pátkem = páteční uzávěrka; značka půlnoci patří k nedělnímu svozu
// (uzávěrka „So 24:00")
const WEEK_TICKS = [{ from: 3, to: 5, text: CUTOFF_FRI }].concat(SUN ? [{ from: 5, to: 7, text: '24:00' }] : []);
// legenda vypisuje jen tóny, které v pásu opravdu jsou
const WEEK_LEGEND = ['ink', 'order', 'ship', 'deliver']
  .map(function (tone, i) {
    return { tone: tone, text: L.legend[i] };
  })
  .filter(function (item) {
    return item.tone === 'ink' || SAT || SUN;
  });

const SCHED_ROWS = L.schedRows({
  cutoff: CUTOFF,
  cutoffFri: CUTOFF_FRI,
  sat: SAT ? SAT.carrier : '',
  sun: SUN ? SUN.carrier : '',
}).filter(function (row) {
  // řádky o zvláštním závozu jen v shopu, kde závoz platí
  return !row.fast || (row.fast === 'sat' ? !!SAT : !!SUN);
});

// Koncová mezera předpony („do ", „nad ") v samostatném span: pás pásem je
// flex, kde se mezera na kraji položky zahodí („do10 kg") — pevná mezera drží.
const keepSpace = (text) => text.replace(/ $/, NBSP);
// Hmotnost limitu bez nul navíc („31,5", ne „31,50") a bez jednotky, tu nese věta.
const kgNum = (kg) =>
  (Math.round(Number(kg) * 100) / 100).toLocaleString(SHOP.shop.language, { maximumFractionDigits: 2 });

// Výměna obsahu slotu bez `replaceChildren` (Safari < 14 / Chrome < 86 ho
// nemají a výjimka v bootu by zastavila zbytek stránky, code review B5-02).
function setChild(slot, child) {
  slot.textContent = '';
  slot.appendChild(child);
}

// --- dopravci a platby ------------------------------------------------------
// Adresa jede k hranici nadměrných balíků, pokud dopravce nemá vlastní strop
// (`shipping.addrLimits`, SK: PPL 31,5 / GLS 39,99 kg)
const addrMax = (carrier) => ADDR_LIMITS[carrier] || LAST;
const CARRIERS = [
  {
    key: 'gls',
    logo: 'gls_logo.svg',
    name: 'GLS',
    logoH: 24,
    limits: L.limits(kgNum(BOX_LIMITS.GLS), kgNum(addrMax('GLS'))),
    text: L.glsText,
  },
  {
    key: 'ppl',
    logo: 'ppl_logo.svg',
    name: 'PPL',
    logoH: 17,
    limits: L.limits(kgNum(BOX_LIMITS.PPL), kgNum(addrMax('PPL'))),
    text: L.pplText,
  },
  {
    key: 'fofr',
    logo: 'fofr_logo.png',
    name: 'Fofr',
    logoH: 23,
    limits: L.limitsOver(LAST),
    text: L.fofrText(LAST, priceText(OVER_PRICE)),
  },
];
// Návrh nese obojí (handover §4): zboží skladem je na prodejně připravené hned
// (rozhodnutí 2026-08-31), potvrzení o připravené OBJEDNÁVCE je ale krok navíc,
// který popis metody v adminu slibuje — nesedí spolu, ale netlučou se.
const PICKUP_TEXT = L.pickupText(PICKUP_PLACE.split(', ').reverse().join(', '), hoursText());

// Ceny plateb Shoptet v API nevydává (`/payment-methods` je nemá) — dobírka
// je `shipping.codFee` z manifestu shopu (CZ 40 Kč ověřená z rekapitulace kroku
// 3 na dev, fixture `dev/checkout-2.html`). Pořadí L.payments: karta, převod,
// dobírka, prodejna.
const COD_FEE = SHOP.shipping.codFee;
const PAYMENTS = L.payments
  .map(function (p, i) {
    return { name: p.name, desc: p.desc, price: i === 2 ? COD_FEE : 0 };
  })
  // platba na prodejně jen v shopu s osobním odběrem (SK ho nemá)
  .filter(function (p, i) {
    return i !== 3 || HAS_PICKUP;
  });


// ---------------------------------------------------------------------------
// Stavební bloky
// ---------------------------------------------------------------------------

// Věta složená z prostého textu a tučných úseků (`{ b: '…' }`) — bez innerHTML.
function rich(node, parts) {
  parts.forEach(function (part) {
    if (typeof part === 'string') node.appendChild(document.createTextNode(part));
    else node.appendChild(el('b', null, part.b));
  });
  return node;
}

function logoImg(file, alt, height, cls) {
  const img = el('img', cls);
  img.src = STATIC_ICO + file;
  img.alt = alt;
  img.loading = 'lazy';
  if (height) img.style.height = height + 'px';
  return img;
}

// Popisek pásma se dvěma délkami: „do 10 kg" na desktopu, „do 10" na mobilu
// (čtyři sloupce na 390 px jednotku neunesou). Jednotka je v samostatném
// span, takže v DOM je text jen jednou.
function bandLabel(node, index, short) {
  const hi = BANDS[index];
  if (index < 0) {
    node.appendChild(el('span', 'bs-shp__b-pre', keepSpace(L.bandOver)));
    node.appendChild(document.createTextNode(String(LAST)));
    node.appendChild(el('span', 'bs-shp__b-kg', KG));
    if (short) node.appendChild(el('span', 'bs-shp__b-plus', '+'));
    return node;
  }
  if (index === 0) node.appendChild(el('span', 'bs-shp__b-pre', keepSpace(L.bandUpTo)));
  node.appendChild(
    document.createTextNode(index === 0 ? String(hi) : BANDS[index - 1] + '–' + hi)
  );
  node.appendChild(el('span', 'bs-shp__b-kg', KG));
  return node;
}

function calcSection() {
  const sec = el('section', 'bs-ip__sec bs-shp__calc-sec');
  sec.appendChild(sectionHead(L.calcTitle, L.calcHint));

  const calc = el('div', 'bs-shp__calc');
  const ctrl = el('div', 'bs-shp__ctrl');

  const wField = el('div', 'bs-shp__field');
  wField.appendChild(el('span', 'bs-shp__flabel', L.calcWeightLabel));
  const wChips = el('div', 'bs-shp__chips');
  CALC_CHIPS.forEach(function (kg) {
    const chip = el('button', 'bs-shp__chip', formatKg(kg));
    chip.type = 'button';
    chip.dataset.bsKg = String(kg);
    chip.setAttribute('aria-pressed', kg === CALC_DEFAULT_KG ? 'true' : 'false');
    wChips.appendChild(chip);
  });
  wField.appendChild(wChips);
  wField.appendChild(el('span', 'bs-shp__fhint', L.calcWeightHint));
  ctrl.appendChild(wField);

  const vField = el('div', 'bs-shp__field');
  vField.appendChild(el('span', 'bs-shp__flabel', L.calcValueLabel));
  const vChips = el('div', 'bs-shp__chips bs-shp__chips--value');
  [
    { free: false, label: CALC_UNDER },
    { free: true, label: CALC_OVER },
  ].forEach(function (opt) {
    const chip = el('button', 'bs-shp__chip', opt.label);
    chip.type = 'button';
    chip.dataset.bsFree = opt.free ? '1' : '0';
    chip.setAttribute('aria-pressed', opt.free ? 'false' : 'true');
    vChips.appendChild(chip);
  });
  vField.appendChild(vChips);
  ctrl.appendChild(vField);

  // výsledek = mobilní řádek pásma z pokladny (handover §1.2). Na desktopu ho
  // nese ovládací panel, na mobilu tentýž řádek uvnitř škály — proto dvě
  // kopie a CSS přepínač, ne dvě různé implementace.
  ctrl.appendChild(el('div', 'bs-shp__cur-slot'));
  calc.appendChild(ctrl);
  calc.appendChild(el('div', 'bs-shp__scale'));
  sec.appendChild(calc);
  return sec;
}

// Přepočet kalkulačky: pásmo z modelu → ukazatel + řádek výsledku. Nad
// posledním pásmem nemá žádný segment `is-cur` a výsledek nese cenu přepravy
// pro nadměrné balíky (tu pokladna nezná, tady je součástí ceníku).
function renderCalc(root, state) {
  const band = bandOf(state.kg);
  if (!band) return;
  const free = band.free && state.free;
  const note = band.over ? WEIGHT_FOOT_OVER : WEIGHT_SCALE_NOTE_A + FREE_FROM + '.';
  const scale = buildWeightScale({
    kg: state.kg,
    band: band,
    note: note,
    free: free,
    overPrice: OVER_PRICE,
  });
  setChild(root.querySelector('.bs-shp__scale'), scale);
  setChild(
    root.querySelector('.bs-shp__cur-slot'),
    weightCurRow(band, { free: free, overPrice: OVER_PRICE }, 'bs-shp__cur')
  );

  root.querySelectorAll('.bs-shp__chip[data-bs-kg]').forEach(function (chip) {
    const on = Number(chip.dataset.bsKg) === state.kg;
    chip.classList.toggle('is-on', on);
    chip.setAttribute('aria-pressed', on ? 'true' : 'false');
  });
  root.querySelectorAll('.bs-shp__chip[data-bs-free]').forEach(function (chip) {
    const on = (chip.dataset.bsFree === '1') === state.free;
    chip.classList.toggle('is-on', on);
    chip.setAttribute('aria-pressed', on ? 'true' : 'false');
  });
}

function initCalc(root) {
  const state = { kg: CALC_DEFAULT_KG, free: false };
  root.querySelector('.bs-shp__calc').addEventListener('click', function (e) {
    const chip = e.target.closest('.bs-shp__chip');
    if (!chip) return;
    if (chip.dataset.bsKg) state.kg = Number(chip.dataset.bsKg);
    else if (chip.dataset.bsFree) state.free = chip.dataset.bsFree === '1';
    else return;
    renderCalc(root, state);
  });
  renderCalc(root, state);
}

// Proporční škála pásem nad ceníkem: šířka sloupce odpovídá šířce pásma
// (poslední, otevřené pásmo dostane 60 % dílu, aby nebylo nekonečné).
function bandsStrip() {
  const cols = BANDS.map(function (hi, i) {
    return (i === 0 ? hi : hi - BANDS[i - 1]) + 'fr';
  })
    .concat([Math.round(BANDS[0] * 0.6) + 'fr'])
    .join(' ');

  const wrap = el('div', 'bs-shp__bands');
  const bar = el('div', 'bs-shp__bands-row');
  // šířky jdou custom property, ne `grid-template-columns` — inline styl by
  // na mobilu přebil přeskládání souhrnů do mřížky 2 × 2
  bar.style.setProperty('--bs-shp-cols', cols);
  BANDS.forEach(function (hi, i) {
    bandLabel(bar.appendChild(el('span', 'bs-shp__band')), i, true);
  });
  bandLabel(bar.appendChild(el('span', 'bs-shp__band bs-shp__band--over')), -1, true);
  wrap.appendChild(bar);

  const ticks = el('div', 'bs-shp__bands-ticks');
  ticks.style.setProperty('--bs-shp-cols', cols);
  [0].concat(BANDS).forEach(function (kg) {
    ticks.appendChild(el('span', null, kg + KG));
  });
  wrap.appendChild(ticks);

  const sum = el('div', 'bs-shp__bands-sum');
  sum.style.setProperty('--bs-shp-cols', cols);
  WEIGHT_BAND_PRICES.forEach(function (p, i) {
    const cell = el('span', 'bs-shp__bsum');
    bandLabel(cell.appendChild(el('span', 'bs-shp__bsum-band')), i, false);
    cell.appendChild(
      // „69 / 119 Kč", ale „€5,19 / €7,59" — symbol vlevo patří ke každé částce
      el('span', 'bs-shp__bsum-price', (currency().left ? priceText(p.box) : number(p.box)) + ' / ' + priceText(p.addr))
    );
    // pásmo 20–30 vozí na výdejní místo jen PPL (GLS má strop 20 kg)
    const limited = i > 0 && BANDS[i - 1] >= BOX_LIMITS.GLS;
    cell.appendChild(el('span', 'bs-shp__bsum-sub', limited ? L.priceSubLimited : L.priceSub));
    if (i === 0 && PROMO) cell.appendChild(el('span', 'bs-shp__bsum-promo', L.sumPromo(PROMO.carrier, priceText(PROMO.price), PROMO.partial ? PROMO.maxKg : null, PROMO.untilLabel)));
    if (i === 0 && PRICE_FREE_SUM) cell.appendChild(el('span', 'bs-shp__bsum-free', PRICE_FREE_SUM));
    sum.appendChild(cell);
  });
  const over = el('span', 'bs-shp__bsum');
  bandLabel(over.appendChild(el('span', 'bs-shp__bsum-band')), -1, false);
  over.appendChild(el('span', 'bs-shp__bsum-price', priceText(OVER_PRICE)));
  over.appendChild(el('span', 'bs-shp__bsum-sub', L.priceSubOver));
  sum.appendChild(over);
  wrap.appendChild(sum);
  return wrap;
}

function priceTable(rows) {
  const table = el('div', 'bs-shp__table');

  const head = el('div', 'bs-shp__thead');
  head.appendChild(el('span', null, L.priceMethod));
  BANDS.forEach(function (hi, i) {
    bandLabel(head.appendChild(el('span')), i, false);
  });
  bandLabel(head.appendChild(el('span')), -1, false);
  table.appendChild(head);

  rows.forEach(function (row) {
    const merge = MOBILE_MERGE[row.key];
    const tr = el('div', 'bs-shp__trow bs-shp__trow--' + row.key + (merge ? ' bs-shp__trow--dup' : ''));

    const name = el('span', 'bs-shp__tname');
    const title = el('span', 'bs-shp__tname-t', row.name);
    // druhý dopravce sloučené řádky se objeví jen na mobilu, kde ta řádka
    // zbyla jediná (viz MOBILE_MERGE)
    const mergedInto = Object.keys(MOBILE_MERGE).find(function (k) {
      return MOBILE_MERGE[k].into === row.key;
    });
    if (mergedInto) {
      const parts = row.name.split(' ');
      title.textContent = '';
      title.appendChild(document.createTextNode(parts[0]));
      title.appendChild(el('span', 'bs-shp__tname-alt', MOBILE_MERGE[mergedInto].suffix));
      title.appendChild(document.createTextNode(' ' + parts.slice(1).join(' ')));
    }
    name.appendChild(title);
    name.appendChild(el('span', 'bs-shp__tname-d', row.desc));
    tr.appendChild(name);

    row.cells.forEach(function (c, i) {
      const cell = el('span', 'bs-shp__tcell');
      bandLabel(cell.appendChild(el('span', 'bs-shp__tcell-band')), i < BANDS.length ? i : -1, true);
      const value = el(
        'span',
        'bs-shp__tprice' + (c.price === null ? ' is-none' : c.price === 0 ? ' is-free' : ''),
        c.price === null ? DASH : c.price === 0 ? L.free : priceText(c.price)
      );
      cell.appendChild(value);
      if (c.promo && c.promo.partial) {
        cell.appendChild(el('span', 'bs-shp__tnote is-promo', L.cellPromoPart(priceText(c.promo.price), kgNum(c.promo.maxKg), c.promo.until)));
      } else if (c.promo) {
        cell.appendChild(el('s', 'bs-shp__twas', priceText(c.promo.was)));
        cell.appendChild(el('span', 'bs-shp__tnote is-promo', L.cellPromo(c.promo.until)));
      }
      if (c.free) cell.appendChild(el('span', 'bs-shp__tnote is-free', PRICE_FREE_CELL));
      else if (c.limit) cell.appendChild(el('span', 'bs-shp__tnote', L.cellLimit(kgNum(c.limit))));
      else if (c.over) cell.appendChild(el('span', 'bs-shp__tnote', L.cellOver(kgNum(c.over))));
      tr.appendChild(cell);
    });
    table.appendChild(tr);
  });
  return table;
}

function priceSection(rows) {
  const sec = el('section', 'bs-ip__sec bs-shp__price-sec');
  const panel = el('div', 'bs-shp__panel');
  const head = el('div', 'bs-shp__panel-head');
  head.appendChild(el('h2', 'bs-shp__panel-h2', L.priceTitle));
  head.appendChild(el('p', 'bs-shp__panel-lead', PRICE_LEAD));
  panel.appendChild(head);
  panel.appendChild(bandsStrip());
  panel.appendChild(priceTable(rows));
  panel.appendChild(el('p', 'bs-shp__panel-note', PRICE_NOTE));
  sec.appendChild(panel);
  return sec;
}

function weekBand() {
  const week = el('div', 'bs-shp__week');

  const days = el('div', 'bs-shp__wdays');
  days.appendChild(el('span'));
  L.weekDays.forEach(function (d) {
    days.appendChild(el('span', null, d));
  });
  week.appendChild(days);

  WEEK_ROWS.forEach(function (row) {
    const line = el('div', 'bs-shp__wrow');
    const label = el('span', 'bs-shp__wlabel');
    if (row.logo) label.appendChild(logoImg(row.logo, row.carrier, row.logoH, 'bs-shp__wlogo'));
    label.appendChild(el('span', 'bs-shp__wlabel-t', row.label));
    line.appendChild(label);
    row.bars.forEach(function (bar) {
      const b = el('span', 'bs-shp__wbar bs-shp__wbar--' + bar.tone);
      b.style.gridColumn = bar.from + 1 + ' / ' + (bar.to + 1);
      b.appendChild(el('span', 'bs-shp__wbar-long', bar.text));
      if (bar.short != null) b.appendChild(el('span', 'bs-shp__wbar-short', bar.short));
      line.appendChild(b);
    });
    week.appendChild(line);
  });

  const ticks = el('div', 'bs-shp__wticks');
  ticks.appendChild(el('span'));
  WEEK_TICKS.forEach(function (t) {
    const span = el('span', null, t.text);
    span.style.gridColumn = t.from + 1 + ' / ' + (t.to + 1);
    ticks.appendChild(span);
  });
  week.appendChild(ticks);

  const legend = el('div', 'bs-shp__wlegend');
  WEEK_LEGEND.forEach(function (item) {
    const row = el('span', 'bs-shp__wleg');
    row.appendChild(el('span', 'bs-shp__wleg-dot bs-shp__wbar--' + item.tone));
    row.appendChild(el('span', null, item.text));
    legend.appendChild(row);
  });
  week.appendChild(legend);
  return week;
}

function schedule() {
  const sched = el('div', 'bs-shp__sched');
  const head = el('div', 'bs-shp__shead');
  L.schedHead.forEach(function (t) {
    head.appendChild(el('span', null, t));
  });
  sched.appendChild(head);

  SCHED_ROWS.forEach(function (row) {
    const line = el('div', 'bs-shp__srow');
    const when = el('span', 'bs-shp__scell');
    when.appendChild(el('span', 'bs-shp__swhen', row.when));
    if (row.whenNote) when.appendChild(el('span', 'bs-shp__ssub', row.whenNote));
    line.appendChild(when);

    const ship = el('span', 'bs-shp__scell');
    ship.appendChild(el('span', 'bs-shp__spre', L.schedPreShip));
    ship.appendChild(el('span', 'bs-shp__sval' + (row.shipNote ? ' is-strong' : ''), row.ship));
    if (row.shipNote) ship.appendChild(el('span', 'bs-shp__ssub', row.shipNote));
    line.appendChild(ship);

    const eta = el('span', 'bs-shp__scell');
    eta.appendChild(el('span', 'bs-shp__spre', L.schedPreEta));
    eta.appendChild(el('span', 'bs-shp__sval' + (row.etaFast ? ' is-fast' : ''), row.eta));
    if (row.etaNote) eta.appendChild(el('span', 'bs-shp__ssub', row.etaNote));
    line.appendChild(eta);
    sched.appendChild(line);
  });

  sched.appendChild(el('div', 'bs-shp__snote', L.schedNote));
  return sched;
}

function etaSection() {
  const sec = el('section', 'bs-ip__sec bs-shp__eta-sec');
  sec.appendChild(sectionHead(L.etaTitle, L.etaHint({ cutoff: CUTOFF, cutoffFri: CUTOFF_FRI })));
  const card = el('div', 'bs-shp__eta');
  card.appendChild(weekBand());
  card.appendChild(schedule());
  sec.appendChild(card);
  return sec;
}

function carrierRow(def) {
  const row = el('div', 'bs-shp__carrier bs-shp__carrier--' + def.key);
  const side = el('span', 'bs-shp__carrier-side');
  if (def.logo) side.appendChild(logoImg(def.logo, def.name, def.logoH, 'bs-shp__carrier-logo'));
  else side.appendChild(el('span', 'bs-shp__carrier-name', def.name));
  side.appendChild(el('span', 'bs-shp__carrier-limits', def.limits));
  row.appendChild(side);
  row.appendChild(rich(el('span', 'bs-shp__carrier-text'), def.text));
  return row;
}

function pickupRow() {
  const row = el('div', 'bs-shp__carrier bs-shp__carrier--pickup');
  const side = el('span', 'bs-shp__carrier-side');
  side.appendChild(el('span', 'bs-shp__carrier-name', L.pickupName));
  side.appendChild(el('span', 'bs-shp__carrier-limits is-free', L.pickupFree));
  row.appendChild(side);

  const body = el('span', 'bs-shp__carrier-body');
  rich(body.appendChild(el('span', 'bs-shp__carrier-text')), PICKUP_TEXT);
  const map = link(PICKUP_MAP_URL, 'bs-shp__map');
  map.target = '_blank';
  map.rel = 'noopener';
  map.appendChild(el('span', null, PICKUP_MAP_LABEL));
  map.appendChild(icon('arrow-right'));
  body.appendChild(map);
  row.appendChild(body);
  return row;
}

function payRow(def) {
  const row = el('div', 'bs-shp__pay');
  const ic = el('span', 'bs-shp__pay-ico');
  ic.appendChild(icon(paymentIcon(def.name)));
  row.appendChild(ic);
  const body = el('span', 'bs-shp__pay-body');
  body.appendChild(el('span', 'bs-shp__pay-name', def.name));
  body.appendChild(el('span', 'bs-shp__pay-desc', def.desc));
  row.appendChild(body);
  row.appendChild(
    el(
      'span',
      'bs-shp__pay-price' + (def.price ? '' : ' is-free'),
      def.price ? priceText(def.price) : L.free
    )
  );
  return row;
}

function columnsSection() {
  const sec = el('section', 'bs-ip__sec bs-shp__cols');

  const left = el('div', 'bs-shp__col');
  left.appendChild(sectionHead(L.carriersTitle, null));
  const carriers = el('div', 'bs-shp__carriers');
  CARRIERS.forEach(function (c) {
    carriers.appendChild(carrierRow(c));
  });
  if (HAS_PICKUP) carriers.appendChild(pickupRow());
  left.appendChild(carriers);
  sec.appendChild(left);

  const right = el('div', 'bs-shp__col');
  right.appendChild(sectionHead(L.payTitle, L.payHint));
  const pays = el('div', 'bs-shp__pays');
  PAYMENTS.forEach(function (p) {
    pays.appendChild(payRow(p));
  });
  right.appendChild(pays);
  sec.appendChild(right);
  return sec;
}

function contactBand() {
  const sec = el('section', 'bs-shp__contact');
  const text = el('div', 'bs-shp__contact-text');
  text.appendChild(el('span', 'bs-shp__contact-title', L.contactTitle));
  text.appendChild(el('span', 'bs-shp__contact-sub', phonesOn() ? L.contactText : L.contactTextNoPhone));
  sec.appendChild(text);

  // kanály v pořadí chat → e-mail → telefon; s chatem e-mail ustoupí (obrys:
  // vstup do chatu je černý, _support-chat.scss, Tomáš 2026-10-04)
  const actions = el('div', 'bs-shp__contact-actions');
  const chat = chatOn();
  if (chat) {
    const btn = link(chatFallbackHref(), 'bs-shp__cbtn bs-shp__cbtn--primary');
    btn.appendChild(icon('chat'));
    btn.appendChild(el('span', null, LC.cta));
    asChatTrigger(btn, 'shipping');
    actions.appendChild(btn);
  }
  const mail = link('mailto:' + CONTACT.email, 'bs-shp__cbtn bs-shp__cbtn--' + (chat ? 'out' : 'primary'));
  mail.appendChild(icon('mail'));
  mail.appendChild(el('span', null, CONTACT.email));
  actions.appendChild(mail);
  if (phonesOn()) {
    const phone = link(telHref(CONTACT.phone), 'bs-shp__cbtn bs-shp__cbtn--ink');
    phone.appendChild(icon('phone'));
    phone.appendChild(el('span', null, CONTACT.phone));
    actions.appendChild(phone);
  }
  sec.appendChild(actions);
  return sec;
}

// ---------------------------------------------------------------------------
export function initShippingPage() {
  if (!pageIs('shipping')) return;
  const host = pageHost();
  if (!host) return;
  if (document.querySelector('.bs-shp')) return; // idempotence

  // ceník je jediná část stránky, kterou umí rozbít změna tarifu — když by
  // model vrátil prázdno, zůstane radši čitelný nativní text z administrace
  const rows = shippingPriceTable();
  if (!rows || !rows.length) return;

  const root = el('div', 'bs-ip bs-shp');
  const hero = buildHero({ eyebrow: LABEL_EYEBROW, title: host.h1, perex: LABEL_PEREX });
  const facts = el('div', 'bs-shp__facts');
  FACTS.forEach(function (fact, i) {
    if (i) facts.appendChild(el('span', 'bs-shp__fdot'));
    facts.appendChild(el('span', 'bs-shp__fact', fact));
  });
  if (PROMO) {
    hero
      .querySelector('.bs-ip__hero-text')
      .appendChild(el('p', 'bs-shp__promo', L.heroPromo(PROMO.carrier, priceText(PROMO.price), PROMO.maxKg, PROMO.untilLabel)));
  }
  hero.querySelector('.bs-ip__hero-text').appendChild(facts);
  root.appendChild(hero);

  // Kalkulačka jde na konec stránky pod dopravce a platbu (zadání Tomáše
  // 2026-09-05, ruší pořadí z handoveru §1, kde stála hned pod hero — viz
  // README handoveru §6).
  root.appendChild(priceSection(rows));
  root.appendChild(etaSection());
  root.appendChild(columnsSection());
  // Kalkulačka ukazuje pásmo a cenu podle hmotnosti — plochý tarif (SK) má
  // jednu cenu do hranice nadměrných balíků, kterou nese už ceník, takže by
  // kalkulačka neměla co spočítat (S4 #400)
  if (HAS_WEIGHT_BANDS) root.appendChild(calcSection());
  root.appendChild(contactBand());

  // Nativní obsah z administrace zůstává v DOM (fallback bez JS) a schová ho
  // CSS pod `html.bs-info-ready` — root proto stojí jako SOUROZENEC `about`.
  host.about.classList.add('bs-ip__native');
  host.about.insertAdjacentElement('beforebegin', root);
  if (HAS_WEIGHT_BANDS) initCalc(root);
  markReady();
}
